import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { Dayjs } from 'plugins/dayjs';
import type {
  SeminarConfigModel,
  SeminarCRUDModel,
  SeminarListModel,
  SeminarReportListModel,
} from 'models/seminar_model';
import {
  getSeminarsService,
  getSeminarsReportService,
  editSeminarService,
  editScheduleSeminarService,
  deleteSeminarService,
  createSeminarService,
  loadConfigSeminarService,
  createSeminarConfigService,
  editSeminarConfigService,
} from 'repositories/seminario_repositorio';

export interface FiltersSeminars {
  name?: string;
  gestion?: string | null;
  studentId?: string | null;
  desde?: string | null;
  hasta?: string | null;
  _limit?: number;
  _page?: number;
}

interface SeminarioState {
  config: SeminarConfigModel | null;
  seminarios: SeminarListModel[] | null;
  count: number | null;
  reports: SeminarReportListModel[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: SeminarioState = {
  config: null,
  seminarios: null,
  count: null,
  reports: null,
  loading: false,
  error: null,
};

export const getSeminars = createAsyncThunk(
  'seminario/getSeminars',
  async (filters: FiltersSeminars, { rejectWithValue }) => {
    try {
      return await getSeminarsService(filters);
    } catch {
      return rejectWithValue('Error al cargar seminarios');
    }
  },
);

export const getSeminarReports = createAsyncThunk(
  'seminario/getSeminarReports',
  async (filters: FiltersSeminars, { rejectWithValue }) => {
    try {
      return await getSeminarsReportService(filters);
    } catch {
      return rejectWithValue('Error al cargar reportes de seminarios');
    }
  },
);

export const editSeminar = createAsyncThunk(
  'seminario/editSeminar',
  async ({ id, data }: { id: string; data: SeminarCRUDModel }, { rejectWithValue }) => {
    try {
      return await editSeminarService(id, data);
    } catch {
      return rejectWithValue('Error al editar seminario');
    }
  },
);

export const editScheduleSeminar = createAsyncThunk(
  'seminario/editScheduleSeminar',
  async (
    {
      id,
      fechaCambio,
      horarios,
    }: {
      id: string;
      fechaCambio: string | Dayjs;
      horarios: Array<{ dia: string; horaInicio: string; horaFin: string }>;
    },
    { rejectWithValue },
  ) => {
    try {
      return await editScheduleSeminarService(id, { horarios, fechaCambio });
    } catch {
      return rejectWithValue('Error al editar horario del seminario');
    }
  },
);

export const deleteSeminar = createAsyncThunk(
  'seminario/deleteSeminar',
  async (id: string, { rejectWithValue }) => {
    try {
      return await deleteSeminarService(id);
    } catch {
      return rejectWithValue('Error al eliminar seminario');
    }
  },
);

export const createSeminar = createAsyncThunk(
  'seminario/createSeminar',
  async (data: SeminarCRUDModel, { rejectWithValue }) => {
    try {
      return await createSeminarService(data);
    } catch {
      return rejectWithValue('Error al crear seminario');
    }
  },
);

export const loadConfigSeminar = createAsyncThunk(
  'seminario/loadConfigSeminar',
  async (year: string | number, { rejectWithValue }) => {
    try {
      return await loadConfigSeminarService(year);
    } catch {
      return rejectWithValue('Error al cargar configuración');
    }
  },
);

export const createConfigSeminar = createAsyncThunk(
  'seminario/createConfigSeminar',
  async (data: SeminarConfigModel, { rejectWithValue }) => {
    try {
      return await createSeminarConfigService(data);
    } catch {
      return rejectWithValue('Error al crear configuración');
    }
  },
);

export const editConfigSeminar = createAsyncThunk(
  'seminario/editConfigSeminar',
  async (data: SeminarConfigModel, { rejectWithValue }) => {
    try {
      return await editSeminarConfigService(data);
    } catch {
      return rejectWithValue('Error al editar configuración');
    }
  },
);

export const seminarioSlice = createSlice({
  name: 'seminario',
  initialState,
  reducers: {
    setListSeminars: (
      state,
      action: PayloadAction<{ rows: SeminarListModel[]; count: number } | null>,
    ) => {
      if (!action.payload) return;
      const data = Array.isArray(action.payload)
        ? { rows: action.payload, count: action.payload.length }
        : action.payload;
      state.seminarios = data.rows;
      state.count = data.count;
    },
    setListSeminarsReports: (
      state,
      action: PayloadAction<{ rows: SeminarReportListModel[]; count: number } | null>,
    ) => {
      if (!action.payload) return;
      const data = Array.isArray(action.payload)
        ? { rows: action.payload, count: action.payload.length }
        : action.payload;
      state.reports = data.rows;
      state.count = data.count;
    },
    setConfigSeminar: (state, action: PayloadAction<SeminarConfigModel | null>) => {
      state.config = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSeminars.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSeminars.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload as { rows: SeminarListModel[]; count: number } | null;
        if (data && !Array.isArray(data)) {
          state.seminarios = data.rows;
          state.count = data.count;
        }
      })
      .addCase(getSeminars.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getSeminarReports.fulfilled, (state, action) => {
        const data = action.payload as { rows: SeminarReportListModel[]; count: number } | null;
        if (data && !Array.isArray(data)) {
          state.reports = data.rows;
          state.count = data.count;
        }
      })
      .addCase(loadConfigSeminar.fulfilled, (state, action) => {
        state.config = action.payload;
      });
  },
});

export const { setListSeminars, setListSeminarsReports, setConfigSeminar } =
  seminarioSlice.actions;
export default seminarioSlice.reducer;
